'use client';

import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

// Type pour les sources de vidéos
type VideoSource =
    | { type: 'local'; src: string }
    | { type: 'youtube'; id: string }
    | { type: 'vimeo'; id: string };

// Type pour les médias (images ou vidéos)
interface MediaItem {
    type: 'image' | 'video';
    alt: string;
    width?: number;
    height?: number;

    // Pour les images
    src?: string;

    // Pour les vidéos
    videoSource?: VideoSource;
    thumbnail?: string; // Miniature pour les vidéos
}

interface MediaGalleryProps {
    media: MediaItem[];
    gridClassName?: string;
    mediaClassName?: string;
    aspectRatio?: 'square' | 'video' | 'auto';
}

export default function MediaGallery({
                                         media,
                                         gridClassName = "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4",
                                         mediaClassName = "rounded-lg hover:opacity-90 transition-opacity cursor-pointer",
                                         aspectRatio = 'square'
                                     }: MediaGalleryProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const openModal = (index: number) => {
        setCurrentIndex(index);
        setIsOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = useCallback(() => {
        setIsOpen(false);
        document.body.style.overflow = 'auto';
    }, []);

    const goToPrevious = useCallback((e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? media.length - 1 : prevIndex - 1));
    }, [media.length]);

    const goToNext = useCallback((e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prevIndex) => (prevIndex === media.length - 1 ? 0 : prevIndex + 1));
    }, [media.length]);

    // Gestion des raccourcis clavier
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'Escape':
                    closeModal();
                    break;
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, closeModal, goToPrevious, goToNext]);

    // Déterminer la classe d'aspect ratio
    const getAspectRatioClass = () => {
        switch (aspectRatio) {
            case 'square':
                return 'aspect-square';
            case 'video':
                return 'aspect-video';
            default:
                return '';
        }
    };

    // Obtenir la source de la miniature pour un élément média
    const getThumbnailSrc = (item: MediaItem): string => {
        if (item.type === 'image') {
            return item.src || '';
        }

        if (item.thumbnail) {
            return item.thumbnail;
        }

        // Générer des miniatures par défaut pour les plateformes de streaming
        if (item.videoSource) {
            if (item.videoSource.type === 'youtube') {
                return `https://img.youtube.com/vi/${item.videoSource.id}/hqdefault.jpg`;
            }
            if (item.videoSource.type === 'vimeo') {
                // Note: Vimeo n'a pas d'URL de miniature directe, il faudrait utiliser leur API
                // Ici on utilise une image générique
                return '/images/video-placeholder.jpg';
            }
        }

        // Image par défaut si aucune miniature n'est disponible
        return '/images/video-placeholder.jpg';
    };

    // Rendu du média dans le modal
    const renderMedia = (item: MediaItem) => {
        if (item.type === 'image' && item.src) {
            return (
                <img
                    src={item.src}
                    alt={item.alt}
                    width={item.width || 1200}
                    height={item.height || 800}
                    className="max-h-[80vh] w-auto object-contain"
                />
            );
        } else if (item.type === 'video' && item.videoSource) {
            switch (item.videoSource.type) {
                case 'local':
                    return (
                        <video
                            src={item.videoSource.src}
                            controls
                            autoPlay
                            className="max-h-[80vh] max-w-full"
                            title={item.alt}
                        >
                            Votre navigateur ne prend pas en charge la lecture de vidéos.
                        </video>
                    );
                case 'youtube':
                    return (
                        <iframe
                            width="853"
                            height="480"
                            src={`https://www.youtube.com/embed/${item.videoSource.id}?autoplay=1`}
                            title={item.alt}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="max-h-[80vh] max-w-full aspect-video"
                        ></iframe>
                    );
                case 'vimeo':
                    return (
                        <iframe
                            src={`https://player.vimeo.com/video/${item.videoSource.id}?autoplay=1`}
                            width="853"
                            height="480"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={item.alt}
                            className="max-h-[80vh] max-w-full aspect-video"
                        ></iframe>
                    );
                default:
                    return <div>Format vidéo non pris en charge</div>;
            }
        }
        return <div>Média non disponible</div>;
    };

    return (
        <>
            {/* Grille de médias */}
            <div className={gridClassName}>
                {media.map((item, index) => (
                    <div
                        key={index}
                        className={cn("relative overflow-hidden", getAspectRatioClass())}
                        onClick={() => openModal(index)}
                    >
                        <img
                            src={getThumbnailSrc(item)}
                            alt={item.alt}
                            className={cn("object-cover", mediaClassName)}
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        />

                        {item.type === 'video' && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                <Play size={48} className="text-white" />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Modal de carrousel */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center"
                    onClick={closeModal}
                >
                    <div
                        className="relative max-w-4xl w-full h-full flex items-center justify-center p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 z-10"
                            onClick={closeModal}
                            aria-label="Fermer"
                        >
                            <X size={24} />
                        </button>

                        <button
                            className="absolute left-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 z-10"
                            onClick={goToPrevious}
                            aria-label="Média précédent"
                        >
                            <ChevronLeft size={24} />
                        </button>

                        <div className="relative w-full max-h-[80vh] flex items-center justify-center">
                            {renderMedia(media[currentIndex])}
                            <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black bg-opacity-50 py-2 px-4">
                                <p className="text-sm md:text-base">{currentIndex + 1} / {media.length}</p>
                                <p className="text-sm md:text-base truncate">{media[currentIndex].alt}</p>
                            </div>
                        </div>

                        <button
                            className="absolute right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 z-10"
                            onClick={goToNext}
                            aria-label="Média suivant"
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
